/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: events entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the events table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the events API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, rodeo_id, event_type_id, event_name, metadata, is_published, event_status, handicap, handicap_type, header_max_handicap, heeler_max_handicap, max_combined_handicap, max_individual_handicap, draw_executed, draw_locked, draw_algorithm, event_format, total_rounds, short_round_size, short_round_count, elimination_enabled, pick_count, draw_count, enforce_mutual_picks, draw_executed_at, draw_locked_by, draw_locked_at, max_contestants, registration_deadline, entry_fee, transaction_fee, allow_no_partner_entry, allow_multiple_registrations, max_registrations_per_user, entry_limits, total_prize_amount, base_prize_amount, slide_type, slide_conversion_rate, slide_base_handicap, is_century_jackpot, is_mixed_gender, buddy_groups_enabled, max_buddy_group_size, trig_event_type_name, created_by, created_at, updated_by, updated_at, contestants_per_performance, draw_results_visible, draw_results_visibility_updated_at, draw_results_visibility_updated_by, judge_sheet_template, judge_sheet_template_id, scoring_state, scoring_went_live_at, scoring_went_live_by, show_results, is_current_live_event, prize_allocation_mode, dynamic_prize_per_entry, entry_fee_prize_portion, display_prize_to_contestants, default_adjustment_overrides, entry_fee_prize_portion_is_percentage, entry_fee_prize_portion_is_fee_deduction, show_prize_amounts_before_complete, show_prize_amounts_in_results, enforce_balanced_positions, allow_pay_later]
 * Searchable fields: []
 * Filterable fields: [id, rodeo_id, event_type_id, judge_sheet_template_id]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  EventsRow,
  EventsInsert,
  EventsUpdate
} from '../../types/generated_schemas/generated-events-schema.js';

export interface EventsQueryOptions extends UnifiedQueryOptions {}

export interface EventsResponse {
  data: EventsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for events table operations
 *
 * @description Provides type-safe access to events API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class EventsClient {
  private readonly basePath = '/events';

  /**
   * Get all events records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of events records
   */
  async getAll(options?: EventsQueryOptions): Promise<EventsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<EventsResponse>(url);
    return response.data;
  }

  /**
   * Get a single events record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the events record
   */
  async getById(id: string): Promise<EventsRow> {
    const response = await httpClient.get<{
      data: EventsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new events record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created events record
   */
  async create(data: EventsInsert): Promise<EventsRow> {
    const response = await httpClient.post<{
      data: EventsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing events record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated events record
   */
  async update(id: string, data: EventsUpdate): Promise<EventsRow> {
    const response = await httpClient.put<{
      data: EventsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a events record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple events records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created events records
   */
  async createMany(data: EventsInsert[]): Promise<EventsRow[]> {
    const response = await httpClient.post<{
      data: EventsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple events records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search events with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: EventsQueryOptions
  ): Promise<EventsResponse> {
    throw new Error('Search operations are not available for events - no searchable fields configured');
  }

  /**
   * Get count of events records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a events record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'events',
      publicFields: ['id', 'rodeo_id', 'event_type_id', 'event_name', 'metadata', 'is_published', 'event_status', 'handicap', 'handicap_type', 'header_max_handicap', 'heeler_max_handicap', 'max_combined_handicap', 'max_individual_handicap', 'draw_executed', 'draw_locked', 'draw_algorithm', 'event_format', 'total_rounds', 'short_round_size', 'short_round_count', 'elimination_enabled', 'pick_count', 'draw_count', 'enforce_mutual_picks', 'draw_executed_at', 'draw_locked_by', 'draw_locked_at', 'max_contestants', 'registration_deadline', 'entry_fee', 'transaction_fee', 'allow_no_partner_entry', 'allow_multiple_registrations', 'max_registrations_per_user', 'entry_limits', 'total_prize_amount', 'base_prize_amount', 'slide_type', 'slide_conversion_rate', 'slide_base_handicap', 'is_century_jackpot', 'is_mixed_gender', 'buddy_groups_enabled', 'max_buddy_group_size', 'trig_event_type_name', 'created_by', 'created_at', 'updated_by', 'updated_at', 'contestants_per_performance', 'draw_results_visible', 'draw_results_visibility_updated_at', 'draw_results_visibility_updated_by', 'judge_sheet_template', 'judge_sheet_template_id', 'scoring_state', 'scoring_went_live_at', 'scoring_went_live_by', 'show_results', 'is_current_live_event', 'prize_allocation_mode', 'dynamic_prize_per_entry', 'entry_fee_prize_portion', 'display_prize_to_contestants', 'default_adjustment_overrides', 'entry_fee_prize_portion_is_percentage', 'entry_fee_prize_portion_is_fee_deduction', 'show_prize_amounts_before_complete', 'show_prize_amounts_in_results', 'enforce_balanced_positions', 'allow_pay_later'],
      searchableFields: [],
      filterableFields: ['id', 'rodeo_id', 'event_type_id', 'judge_sheet_template_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'trig_event_type_name', 'created_at', 'updated_at'],
      requiredFields: ['id', 'draw_results_visible', 'scoring_state', 'show_results', 'is_current_live_event', 'prize_allocation_mode', 'dynamic_prize_per_entry', 'entry_fee_prize_portion', 'display_prize_to_contestants', 'default_adjustment_overrides', 'entry_fee_prize_portion_is_percentage', 'entry_fee_prize_portion_is_fee_deduction', 'show_prize_amounts_before_complete', 'show_prize_amounts_in_results', 'enforce_balanced_positions'],
    };
  }
}

// Export singleton instance for convenience
export const eventsClient = new EventsClient();
